import isEqual from "lodash-es/isEqual";
import { DEFAULT_BASE_URL, HELPER_NAMES } from "../../api-sdk/constants";
import { BeamBaseAPIConfig } from "../../api-sdk/types";
import { postCart } from "../../api-sdk/v3/routes";
import { debounce, memoizeLast } from "../../utils";
import { BeamError } from "../../utils/beam-errors";
import { BEAM_CART_COOKIE_NAME, TBeamCartLocalStorage, TCartApi } from "../../shared/cart-contents";
import { setCookieValue } from "../../utils/cookies";
import { BeamCartChangeEvent, BeamCartCreatedEvent, BeamWidgetStoredPromoCodesEvent } from "../../utils/events";
import { createScopedLocalStorage } from "../../utils/local-storage";
import { getRemoteSession } from "../../utils/remote-session";
import { getBeamCartId } from "../../utils/cart";
import { formatStoreAndReturnPromoCodes } from "../../utils/promoManager";

export type BeamCartAPIConfig = BeamBaseAPIConfig & {
  storeId: number;
  domain?: string; // Domain to set cookies on
  statsigExperiment?: string;
};

export type BeamCartValues = {
  cartId?: string; // External (partner) cart ID
  itemCount?: number;
  subtotal?: number;
  currencyCode?: string;
} & Partial<TCartApi>;

/**
 * The debounce time in milliseconds for updating the cart - updateCart will delay until it hasn't been called for
 * this long before handling the last call.
 */
const UPDATE_CART_DEBOUNCE_MS = 50;

/**
 * Sends cart information to Beam
 * Memoized based on previous arguments - if the objects are deep-equal, no update is made.
 * @side-effects Sets beam_cart cookie, set cart data in localstorage
 * @example
 * const BeamConfig = { apiKey: 'abc-123' }
 * const { beamCartId } =  await updateCart(BeamConfig, { beamCartId, itemCount, subtotal, currencyCode })
 * */
const updateCart = debounce(
  memoizeLast(async function updateCart(
    config: BeamCartAPIConfig,
    cartValues: BeamCartValues,
    isFirstCartUpdateOnPageLoad = false,
  ) {
    try {
      const localStorage = createScopedLocalStorage({ apiKey: config.apiKey });
      const baseUrl = config.baseUrl ?? DEFAULT_BASE_URL;

      // The cart cache is used to detect changes after page refreshes / across tabs
      const cachedCart: TBeamCartLocalStorage | null = localStorage.getItemJson("cart");
      // The Beam cart cookie is used to identify the cart across subdomains
      // Use explicit null if empty because API requires it
      const beamCartId: string | null = getBeamCartId(BEAM_CART_COOKIE_NAME, config);
      // Remote session ID (ie, device ID from an A/B testing tool) will be linked to the cart
      const remoteSessionId: string | undefined = getRemoteSession(config) ?? undefined;

      const {
        cartId,
        subtotal,
        itemCount,
        currencyCode,
        content: { items = [], discounts = [] } = {},
        schema = { source: "generic" },
      } = cartValues;

      const cartWithDefaults = {
        cartId,
        subtotal,
        itemCount,
        currencyCode,
        content: { items, discounts },
        schema,
        beamCartId,
      };

      // If this is our first time calling "updateCart" on page load, and there is
      // no prior "cachedCart", and the current Platform cart is not empty,
      // we should assume that the cart has not been interacted with.
      // In this case, we want to cache the current cart, but not send it to Beam
      // so that the next time we call this method, it will compare to the existing
      // Platform cart
      if (isFirstCartUpdateOnPageLoad && !cachedCart && !!cartValues?.itemCount) {
        // logger.debug(
        //   "[update-cart] first attempt to update cart; no prior cart found, cart is not empty; skipping update to Beam"
        // );
        localStorage.setItemJson("cart", {
          ...cartWithDefaults,
          beamCartId: beamCartId,
        });

        return;
      }

      // Early return conditions. Skip sending cart to Beam if any apply:
      // 1. The cart has not changed
      // 2. Cart is empty, unless the cart was not empty before
      if (
        isEqual(cartWithDefaults, {
          ...cachedCart,
          cartId: cachedCart?.cartId /* make sure optional key is defined even if empty */,
        }) || // cart has not changed, OR
        (cartValues.itemCount === 0 && // nothing in current cart, AND
          (!beamCartId || cachedCart?.itemCount === 0)) // no/expired prev cart or prev cart was empty
      ) {
        // logger.debug("[update-cart] cart not changed");
        // If the cart has not changed because the cart is empty now,
        // we still want to save that this changed happened in our cached cart
        // so the next update will compare against a current cart
        localStorage.setItemJson("cart", {
          ...cartWithDefaults,
          beamCartId: beamCartId,
        });

        return;
      }

      formatStoreAndReturnPromoCodes(
        (discounts ?? []).map((discount) => ({ value: discount?.code })),
        config.apiKey,
      );
      window.dispatchEvent(new BeamWidgetStoredPromoCodesEvent({ source: HELPER_NAMES.cart_update }));
      // logger.debug("[update-cart] sending cart update request");
      const response = await postCart({
        baseUrl,
        headers: {
          authorization: `Api-Key ${config.apiKey}`,
        },
        requestBody: {
          beamCartId, // backwards compat: explicitly sending null here is required to get initial beamCartId
          storeId: config.storeId,
          remoteSessionId,
          cartId,
          subtotal,
          itemCount,
          currencyCode,
        },
      });

      // Response will contain a new beamCartId if one was not provided, or old one expired
      const { beamCartId: newBeamCartId, expiresAt, beamCartCreated } = response;

      if (newBeamCartId && expiresAt) {
        await setCookieValue({
          name: BEAM_CART_COOKIE_NAME,
          domain: config.domain,
          value: newBeamCartId,
          path: "/",
          // If we don't set an expiration, the cookie will be cleared when the tab is closed
          expires: new Date(expiresAt).getTime(),
        });
      }

      localStorage.setItemJson("cart", {
        ...cartWithDefaults,
        beamCartId: newBeamCartId,
      });

      const eventDetail = {
        schema: schema as any, // TS union type can't figure out what variant applies here
        content: {
          items: items as any,
          discounts: discounts as any,
        },
        subtotal,
        itemCount,
        currencyCode,
        cartId,
        beamCartId: newBeamCartId,
      };

      // emit change event on any create/update/delete of cart items
      window.dispatchEvent(new BeamCartChangeEvent(eventDetail));

      // emit created event only when the cart is new according to Beam
      if (beamCartCreated) {
        window.dispatchEvent(new BeamCartCreatedEvent(eventDetail));
      }
    } catch (err) {
      throw new BeamError("Could not update cart in Beam", { cause: err });
    }
  }),
  UPDATE_CART_DEBOUNCE_MS,
  { leading: true }, // handle first call immediately but delay for more calls within debounce window
);

export { updateCart };
